// @flow
import map from 'lodash/fp/map';
import uniq from 'lodash/fp/uniq';
import pipe from 'lodash/fp/pipe';
import last from 'lodash/fp/last';
import sortBy from 'lodash/fp/sortBy';
import uniqBy from 'lodash/fp/uniqBy';
import compact from 'lodash/fp/compact';
import flatten from 'lodash/fp/flatten';
import flatMap from 'lodash/fp/flatMap';
import memoize from 'lodash/fp/memoize';
import first from 'lodash/fp/first';
import get from 'lodash/fp/get';
import sum from 'lodash/fp/sum';
import type { _Iteratee } from 'lodash';
import invariant from 'invariant';
import orderBy from 'lodash/fp/orderBy';

import { getField } from './model';
import { toDate, latest, isLater, isAfterOrSame } from './date';
import { typedIdentityFilter, interleaved } from './array';
import { mergedTagTrees } from './tag-tree';
import { renderPriceRange, rangeForPriceList } from './price';
import { byName } from './technology';
import {
  technologies as catalogTechnologies,
  type ResolvedCatalogAtsType,
} from './catalog';
import {
  items,
  featuresWithoutFeature,
  mergedFeatureLists,
  flattenedFeatureListWithParent,
} from './feature';
import { tagTreeHighlights, type Highlight } from './highlight';
// types
import type { ImageTypeBucket, ImageRecord } from './images';
import type { Customer } from './customer';
import type { PriceRange, PriceParams } from './price';
import type { TagTree, TagValue } from './tag-tree';
import type { Technology } from './technology';
import type { Catalog } from './catalog';
import type { Feature } from './feature';
import type { HighlightSpec } from './highlight';
import {
  type SerializedFacetMap,
  type Facet,
  deserializedFacetList,
} from './facet';
import type { ObjectMovie } from './object-movie';
import type { Index } from './search-engine';
import type { ROA, ROE } from './type';

import {
  DEFAULT_AMOUNT_RANGE,
  amountForGroup,
  type MonetaryAmount,
  type MonetaryAmountRange,
  type MonetaryAmountRangeByPurpose,
  type PriceGroupToMonetaryAmountMap,
} from 'modules/monetary-amount';
import {
  catalogAtsType,
  type AbstractCatalog,
  defaultProductReleaseDate,
} from 'modules/abstract-catalog';
// TODO: Resolve this
// eslint-disable-next-line import/no-cycle
import {
  type SerializedItemStockShipmentRecord,
  type ItemShipmentStockRecord,
  type AllocationStrategy,
  deserializedItemStockRecord,
  DEFAULT_WAREHOUSE_ID,
} from 'modules/inventory';
import { type TranslateType } from 'ui/l10n-context';
import { type Row as CsvRow } from 'modules/csv';
import type { EmbellishmentProvider } from 'modules/product-embellishment-provider';

// TODO: Resolve this
// eslint-disable-next-line import/no-cycle
export { availabilityMessageDate } from 'modules/availability';

/**
 * @module modules/product
 */

export const REVISIONS_FEATURE_NAME = 'Post Catalog Revisions';
export const TECHNOLOGY_TAG_NAME = 'Technology';

/**
 * The method by which inventory stock should be calculated for the `StockItem` in the case
 * where the `StockItem` has `components`.
 *
 * 'bundle': The `StockShipment`s of the `StockItem` itself should be used, just like normal.
 * 'components': The `StockShipment`s of the various `StockItemComponent`s should be assesed
 *   to find cumulative stock across all components.
 */
export type StockItemComponentInventoryMode = 'bundle' | 'components';

/**
 * A reference to a component stock item, part of a product bundle.
 */
export type StockItemComponent = $ReadOnly<{|
  product_number: string,
  variation_code: string,
  // a `StockItem` key
  key: string,
  // this does **not** indicate that these must be purchased as a set, it indicates how many
  // units of this component are allocated per unit selected of the parent bundle when
  // `inventory_mode` is set to `'components'`.
  sold_in_multiples_of: ?number,
|}>;

/**
 * A stock item.
 */
export type StockItem = $ReadOnly<{|
  _id: string,
  key: string,
  upc: string,
  sku: string,
  name: string,
  alt_name?: null | string,
  prices: PriceGroupToMonetaryAmountMap,
  position: number,
  pending?: boolean,
  client_fields?: { 'Pending UPC'?: boolean },
  sold_in_multiples_of?: number,
  inventory_mode?: StockItemComponentInventoryMode,
  components?: $ReadOnlyArray<StockItemComponent>,
|}>;

/**
 * A variation.
 */

type ProductNumber = string;
export type Variation = $ReadOnly<{|
  _id: string,
  name: string,
  code: string,
  base_color: string,
  complementary: null | $ReadOnlyArray<ProductNumber>,
  similar: null | $ReadOnlyArray<ProductNumber>,
  images: null | ImageTypeBucket,
  stock_items: $ReadOnlyArray<StockItem>,
  available_on: string | null,
  ats_date?: string | null,
  ats_days?: number | null,
  ats_type?: 'B' | 'N' | 'L' | null,
  is_dropped: boolean,
  tags: TagTree,
  client_fields: $ReadOnly<{
    design_elements?: $ReadOnlyArray<string>,
    artifi_product_code?: string,
    artifi_sku?: string,
    ...
  }>,
  features?: null | $ReadOnlyArray<Feature>,
  filtered?: boolean,
  can_embellish?: boolean,
  primary_warehouse?: string,
  position: number,
  permissions: ?$ReadOnlyArray<string>,
  embellishment_strategy?: EmbellishmentProvider,
|}>;

/**
 * A product.
 */
export type Product = $ReadOnly<{|
  _id: string,
  name: string,
  number: string,
  catalog_key: string,
  /**
   * The position of this Product in the containing catalog.
   */
  position: number,
  price_range: PriceRange,
  variations: Variation[],
  tags: TagTree,
  primary_warehouse?: string,
  tags: TagTree,
  client_fields: { status?: string, division?: string },
  features?: $ReadOnlyArray<Feature> | null,
  sold_in_multiples_of?: number,
  minimum_purchase?: number,
  description: string | null,
  region: string | null,
|}>;

export type SerializedProduct = Product;

export type VariationPair = [Product, Variation];

export type StockItemTriple = [Product, Variation, StockItem];

export type VariationPriceRenderer = (Variation) => string;

export type ProductsQuery = $ReadOnly<{
  keyword?: string,
  catalog?: string,
  custom_catalog?: string,
  customer?: string,
  dropped?: boolean,
  filterNotOrderable?: boolean,
  lastOrderableDate?: string | null,
  productNumbers?: $ReadOnlyArray<string>,
  componentNumbers?: $ReadOnlyArray<string>,
  keys?: $ReadOnlyArray<string>,
  search?: string | 'has_primary_images',
  ship_on?: string | null,
  [string]: string | $ReadOnlyArray<string>,
}>;

export type ProductsQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<Product>,
  stockShipments: $ReadOnlyArray<ItemShipmentStockRecord>,
  facets: $ReadOnlyArray<Facet>,
  total: number,
|}>;

export type SerializedProductsQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<SerializedProduct>,
  stock_shipments: $ReadOnlyArray<SerializedItemStockShipmentRecord> | null,
  facets: SerializedFacetMap,
  range: string,
|}>;

export type SerializedVariationsQueryResponse = $ReadOnly<{|
  results: $ReadOnly<{|
    products: $ReadOnlyArray<SerializedProduct>,
    variations: $ReadOnlyArray<
      $ReadOnly<{| code: string, product_id: string |}>,
    >,
  |}>,
  stock_shipments: $ReadOnlyArray<SerializedItemStockShipmentRecord> | null,
  facets: SerializedFacetMap,
  range: string,
|}>;

export type VariationsQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<VariationPair>,
  response: SerializedVariationsQueryResponse,
  facets: $ReadOnlyArray<Facet>,
  total: number,
|}>;

export type ObjectMovieQuery = $ReadOnly<{|
  productNumber: string,
  variationCode: string,
|}>;

export type ObjectMovieQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<ObjectMovie>,
  facets: $ReadOnlyArray<Facet>,
|}>;

export type SearchableVariationPair = $ReadOnly<{|
  id: string,
  productNumber: string,
  productName: string,
  variationName: string,
  variationCode: string,
|}>;

export type Comparator = (Product) => string | number;
export type VariationPairComparator = (VariationPair) => string | number;

/**
 * The different methods by which we can sort Products and VariationPairs.
 */
export enum SortOrder of string {
  Catalog,
  Name,
  Price,
  ProductNumber,
}

/**
 * Returns the component parts of a size name.
 *
 * @memberof modules/product
 */
export const sizeParts = (stockItem: StockItem): string[] =>
  ['name', 'altName'].map((f) => getField(stockItem, f));

/**
 * Assembles a size name from a StockItem.
 *
 * @memberof modules/product
 * @param {Object} params
 * @param {StockItem} params.stockItem
 * @param {string} params.separator='x'
 */

type SizeNameParams = ROE<{
  stockItem: StockItem,
  separator: string,
}>;

export const sizeName = ({ stockItem, separator }: SizeNameParams): string =>
  sizeParts(stockItem)
    .reduce((acc, part) => (part ? [...acc, part] : acc), [])
    .join(separator);

export const stockItemHasName = ({
  stockItem,
  name,
  altName,
}: {
  stockItem: StockItem,
  name: string,
  altName: string | null,
}): boolean =>
  stockItem.name === name && (stockItem.alt_name || null) === altName;

export const sortedStockItems = (
  list: $ReadOnlyArray<StockItem>,
): $ReadOnlyArray<StockItem> =>
  sortBy(([(si) => si.position]: _Iteratee<StockItem>[]))(list);

export const variationStockItems = (
  variation: Variation,
): $ReadOnlyArray<StockItem> => sortedStockItems(variation.stock_items);

export const productStockItems = (
  product: Product,
): $ReadOnlyArray<StockItem> =>
  pipe(
    // eslint-disable-next-line no-use-before-define
    variations,
    flatMap(variationStockItems),
  )(product);

/**
 * Returns array of size names for a variation.
 *
 * @memberof modules/product
 * @param {Variation} variation
 */
export const variationSizeNames = (
  variation: Variation,
  separator: string,
): string[] =>
  variationStockItems(variation).map((stockItem) =>
    sizeName({ stockItem, separator }),
  );

export const variationStockItemKeys = (
  variation: Variation,
): $ReadOnlyArray<string> =>
  variationStockItems(variation).map((stockItem) => stockItem.key);

export const stockItemIsPending = (stockItem: StockItem): boolean =>
  stockItem.pending ||
  !!(stockItem.client_fields && stockItem.client_fields['Pending UPC']);

export const variationIsPending = ({
  variation,
}: {
  variation: Variation,
}): boolean => variation.stock_items.some(stockItemIsPending);

export const primaryWarehouseForPair = ([product, variation]: VariationPair):
  | string
  | 'DEFAULT_WAREHOUSE_ID' =>
  variation.primary_warehouse ||
  product.primary_warehouse ||
  DEFAULT_WAREHOUSE_ID;

export const variationKey = ({
  product,
  variation,
}: {
  product: Product,
  variation: Variation,
}): string => [product.number, variation.code].join(':');

export const findPosition = (obj: any = { ...null }): number =>
  obj.position ||
  (obj.product && obj.product.position) ||
  (obj.stockItem && obj.stockItem.position) ||
  (obj.variation && obj.variation.position) ||
  (obj.page && obj.page.position) ||
  Infinity;

export const unitPriceRange =
  (priceGroup: string): ((Product) => MonetaryAmountRange | null) =>
  ({ price_range: [lowMap, highMap] }: Product): MonetaryAmountRange | null => {
    const low = amountForGroup(lowMap, priceGroup);
    const high = amountForGroup(highMap, priceGroup);

    if (low !== null && high !== null) return { low, high };
    if (low !== null && high === null) return { low, high: low };
    if (low === null && high !== null) return { low: high, high };
    return null;
  };

const stockItemPrice =
  (priceGroup: string) =>
  ({ prices }: StockItem): MonetaryAmount | null =>
    amountForGroup(prices, priceGroup);

export const variationStockItemsRange =
  (
    priceGroup: string,
  ): ((variation: Variation) => MonetaryAmountRange | null) =>
  (variation: Variation): MonetaryAmountRange | null => {
    const itemAmount = stockItemPrice(priceGroup);
    if (!variation.stock_items[0]) return DEFAULT_AMOUNT_RANGE;

    const itemAmounts = variation.stock_items.map(itemAmount).filter(Boolean);
    if (itemAmounts.length === 0) return null;

    return {
      low: Math.min(...itemAmounts),
      high: Math.max(...itemAmounts),
    };
  };

export const variationTypedUnitPriceRange =
  ({
    retail_price_group: rGroup,
    wholesale_price_group: wGroup,
  }: Customer): ((variation: Variation) => MonetaryAmountRangeByPurpose) =>
  (variation: Variation): MonetaryAmountRangeByPurpose => {
    const wholesale = variationStockItemsRange(wGroup)(variation);
    invariant(wholesale !== null, 'Variation must have wholesale prices');

    return {
      retail: variationStockItemsRange(rGroup)(variation),
      wholesale,
    };
  };

export const unitPriceRangeByPurpose =
  ({
    retail_price_group: rGroup,
    wholesale_price_group: wGroup,
  }: Customer): ((product: Product) => MonetaryAmountRangeByPurpose) =>
  (product: Product): MonetaryAmountRangeByPurpose => {
    const wholesale = unitPriceRange(wGroup)(product);
    invariant(wholesale !== null, 'Product must have wholesale prices');

    return {
      retail: unitPriceRange(rGroup)(product),
      wholesale,
    };
  };

export const stockItemPricesForVariation = (
  variation: Variation,
): PriceGroupToMonetaryAmountMap[] =>
  variation.stock_items.map((si) => si.prices);

export const firstStockItemForVariation = (variation: Variation): StockItem => {
  const stockItem = first(variation.stock_items);

  invariant(stockItem, 'variation must have a stock item');

  return stockItem;
};

export const variationPriceRendererForParams =
  (params: PriceParams): VariationPriceRenderer =>
  (variation: Variation) =>
    renderPriceRange({
      range: rangeForPriceList({
        prices: stockItemPricesForVariation(variation),
      }),
      ...params,
    });

export const variationsInPositionOrder: (
  $ReadOnlyArray<Variation>,
) => $ReadOnlyArray<Variation> = (variations) =>
  sortBy((variation) => variation.position)(variations);

export const variations = (product: Product): $ReadOnlyArray<Variation> =>
  variationsInPositionOrder(product.variations);

export const heroVariation: (product: Product) => Variation = memoize(
  (product: Product): Variation => variations(product)[0],
);
export const variationGivenCode = (
  variationCode: string,
  product: Product,
): Variation | null =>
  variations(product).find(({ code }) => variationCode === code) || null;

export const featureByName = (
  features: $ReadOnlyArray<Feature> = [],
  name: string,
): Feature | null =>
  features.reduce((found, f) => {
    if (found) return found;

    if (f === name) return f;

    if (typeof f === 'string') return null;

    if (f.name === name) return f;

    return featureByName(f.items, name);
  }, null);

export const pairFeatures = ([
  product,
  variation,
]: VariationPair): $ReadOnlyArray<Feature> =>
  mergedFeatureLists(product.features || [], variation.features || []);

export const productAndAllVariationsFeatures = (
  product: Product,
): $ReadOnlyArray<Feature> =>
  mergedFeatureLists(
    product.features || [],
    flatten(variations(product).map((v) => v.features || []) || []),
  );

export const productSizeNames = (
  product: Product,
  separator: string,
): string[] =>
  pipe(
    variations,
    flatMap((v: Variation): $ReadOnlyArray<string> =>
      variationSizeNames(v, separator),
    ),
    uniq,
  )(product);

export const productRevisions = (product: Product): $ReadOnlyArray<Feature> =>
  items(featureByName(product.features || [], REVISIONS_FEATURE_NAME));

export const pairNormalFeatures = (
  pair: VariationPair,
): $ReadOnlyArray<Feature> =>
  featuresWithoutFeature(pairFeatures(pair), REVISIONS_FEATURE_NAME);

export const pairRevisions = (pair: VariationPair): $ReadOnlyArray<Feature> =>
  items(featureByName(pairFeatures(pair), REVISIONS_FEATURE_NAME));

export const productAndAllVariationsRevisions = (
  product: Product,
): $ReadOnlyArray<Feature> =>
  items(
    featureByName(
      productAndAllVariationsFeatures(product),
      REVISIONS_FEATURE_NAME,
    ),
  );

export const productAndAllVariationsHaveRevisions = (
  product: Product,
): boolean => productAndAllVariationsRevisions(product).length > 0;

export const pairHasRevisions = (pair: VariationPair): boolean =>
  pairRevisions(pair).length > 0;

export const pairTagTree = ([product, variation]: VariationPair): TagTree =>
  mergedTagTrees(product.tags, variation.tags);

export const technologyTagValues = (pair: VariationPair): TagValue =>
  pairTagTree(pair)[TECHNOLOGY_TAG_NAME] || [];

export const technologies = (
  pair: VariationPair,
  allTechnologies: Technology[],
): Technology[] =>
  pipe([
    technologyTagValues,
    (tagValue: TagValue): $ReadOnlyArray<string> =>
      Array.isArray(tagValue) ? tagValue : [],
    map((stringTagValue: string) => byName(allTechnologies, stringTagValue)),
    typedIdentityFilter,
  ])(pair);

export const technologiesGivenCatalog = (
  pair: VariationPair,
  catalog: Catalog,
): Technology[] => technologies(pair, catalogTechnologies(catalog));

export const basicSizeNames = (variation: Variation): string[] =>
  variationStockItems(variation).map(({ name }) => name);

export const pairKey = ([{ number }, { code }]: VariationPair): string =>
  `${number}:${code}`;

export const pairsFromFilteredProducts = (
  products: $ReadOnlyArray<Product>,
): $ReadOnlyArray<VariationPair> =>
  flatMap(
    (product: Product): $ReadOnlyArray<[Product, Variation]> =>
      product.variations.map((v) => [product, v]),
    products,
  ).filter(([, { filtered }]: VariationPair) => !filtered);

export const accessibleVariations = (product: Product): Variation[] =>
  variations(product).filter(({ filtered }) => !filtered);

export const pairsForCatalogRetainingAtLeastOne = (
  desiredCatalogKey: string,
  pairs: $ReadOnlyArray<VariationPair>,
): $ReadOnlyArray<VariationPair> =>
  pipe(
    // eslint-disable-next-line camelcase
    sortBy(([{ catalog_key }]) => (catalog_key === desiredCatalogKey ? -1 : 1)),
    uniqBy((pair) => pairKey(pair)),
  )(pairs);

export const division = (product: Product): string | null =>
  (product.client_fields || { ...null }).division || null;

export const clientFieldsAsTagTree = (
  clientFields: $ReadOnly<{ [string]: mixed }>,
): TagTree =>
  ((((Object.entries(clientFields): any): [string, mixed][]).filter(
    ([_key, value]) =>
      typeof value === 'string' ||
      (Array.isArray(value) && value.every((v) => typeof v === 'string')),
  ): any): [string, string | string[]][])
    .map(([key, value]): [string, string[]] => [
      key,
      Array.isArray(value) ? value : [value],
    ])
    .reduce(
      (acc, [key, value]: [string, string[]]) => ({ ...acc, [key]: value }),
      { ...null },
    );

export const tagTreeWithClientFields = (product: Product): TagTree =>
  mergedTagTrees(product.tags, clientFieldsAsTagTree(product.client_fields));

export const pairTagTreeWithClientFields = ([
  product,
  variation,
]: VariationPair): TagTree =>
  mergedTagTrees(
    pairTagTree([product, variation]),
    mergedTagTrees(
      clientFieldsAsTagTree(product.client_fields),
      clientFieldsAsTagTree(variation.client_fields),
    ),
  );

export const highlightsForVariationPair =
  (
    highlightSpecs: $ReadOnlyArray<HighlightSpec>,
  ): ((variationPair: VariationPair) => $ReadOnlyArray<Highlight>) =>
  (variationPair: VariationPair) =>
    tagTreeHighlights(highlightSpecs)(
      pairTagTreeWithClientFields(variationPair),
    );

export const topHighlightForVariationPair =
  (
    highlightSpecs: $ReadOnlyArray<HighlightSpec>,
  ): ((pair: VariationPair) => Highlight | null) =>
  (pair: VariationPair): Highlight | null =>
    first(highlightsForVariationPair(highlightSpecs)(pair));

export const stockItemByKey = (
  variation: Variation,
  stockItemKey: string,
): StockItem | null =>
  variation.stock_items.find(({ key }) => key === stockItemKey) || null;

export const productNumberGivenStockItemKey = (
  products: Product[],
  stockItemKey: string,
): null | string => {
  // eslint-disable-next-line fp/no-let, fp/no-loops, no-plusplus, fp/no-mutation
  for (let idx = 0; idx < products.length; idx++) {
    if (
      productStockItems(products[idx])
        .map((si) => si.key)
        .includes(stockItemKey)
    ) {
      return products[idx].number;
    }
  }
  return null;
};

export const variationIsEmbellishable = (variation: Variation): boolean =>
  variation.can_embellish || false;

/**
 * @memberof modules/product
 */
export const minimumPurchase = (product: Product): number =>
  (getField(product, 'minimum_purchase'): number);

/**
 * @memberof modules/product
 *
 * Default value is 1.
 */
export const soldInMultiplesOfGivenKey = (
  variation: Variation,
  stockItemKey: string,
): number =>
  (getField(
    stockItemByKey(variation, stockItemKey),
    'sold_in_multiples_of',
  ): number) || 1;

export const productsQueryAsQueryString = ({
  filterNotOrderable,
  lastOrderableDate,
  ...query
}: ProductsQuery): string => {
  const params = new URLSearchParams();
  const calculateValue = (name: string, value: string) =>
    name === 'ship_on' &&
    filterNotOrderable &&
    lastOrderableDate &&
    isLater(toDate(value), toDate(lastOrderableDate))
      ? lastOrderableDate
      : value;

  Object.entries(query)
    // TODO: map returns mixed values always
    .map(([name, value]: [string, any]): [string, string] => [name, value])
    .forEach(([name, value]) => {
      if (name === 'productNumbers') {
        (value: any).forEach((number: string) => {
          params.append('product_numbers[]', number);
        });
        return;
      }

      if (name === 'componentProductNumbers') {
        (value: any).forEach((number: string) => {
          params.append('component_product_numbers[]', number);
        });
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((subvalue: string) =>
          params.append(
            name.includes('[]') ? name : `${name}[]`,
            calculateValue(name, subvalue),
          ),
        );
      } else {
        const calculatedValue = calculateValue(name, value);

        if (
          calculatedValue !== null &&
          typeof calculatedValue !== 'undefined'
        ) {
          params.append(name, calculatedValue);
        }
      }
    });

  if (
    filterNotOrderable &&
    lastOrderableDate &&
    params.get('ship_on') === null
  ) {
    params.append('ship_on', lastOrderableDate);
    params.append('availability', 'true');
  }

  params.append('hoist_quantities', 'true');

  return params.toString();
};

export const deserializedProductsQueryResponse = ({
  results,
  stock_shipments: stockShipments,
  facets,
  range,
}: SerializedProductsQueryResponse): ProductsQueryResponse => ({
  results,
  stockShipments: (stockShipments || []).map(deserializedItemStockRecord),
  facets: deserializedFacetList(facets),
  total: Number(last(range.split('/'))),
});

export const deserializedVariationsQueryResponse = (
  response: SerializedVariationsQueryResponse,
): VariationsQueryResponse => {
  const {
    results: { products, variations: resultVariations },
    facets,
    range,
  } = response;
  return {
    results: resultVariations.map((variationEntry): VariationPair => {
      const variationCode = variationEntry.code;
      const productId = variationEntry.product_id;

      const product = products.find(({ _id: id }) => id === productId);

      invariant(
        product,
        'Product with id %s must exist in result set',
        productId,
      );

      const variation = product.variations.find(
        ({ code }) => code === variationCode,
      );

      invariant(
        variation,
        'Variation with code %s must exist in product %s',
        variationCode,
        product.number,
      );

      return [product, variation];
    }),
    facets: deserializedFacetList(facets),
    total: Number(last(range.split('/'))),
    response,
  };
};

export const hasAlternateSizes = (variation: Variation): boolean =>
  variation.stock_items.some((si) => !!si.alt_name);

export const distinctSizeNames = (
  variation: Variation,
): $ReadOnlyArray<string> => uniq(basicSizeNames(variation));

export const distinctAlternateNames = (
  variation: Variation,
): $ReadOnlyArray<string> =>
  uniq(
    variationStockItems(variation)
      .map((si) => si.alt_name)
      .filter(Boolean),
  );

export const distinctSizeNamesByPosition = (
  variation: Variation,
): $ReadOnlyArray<string> =>
  distinctAlternateNames(variation)
    .reduce(
      (acc, alt) =>
        interleaved(
          (a, b) => a.name === b.name,
          acc,
          sortedStockItems(
            variation.stock_items.filter((si) => si.alt_name === alt),
          ),
        ),
      [],
    )
    .map((si) => si.name);

export const variationImages = (
  variation: Variation,
): $ReadOnlyArray<ImageRecord> => [
  ...(variation.images?.P || []),
  ...(variation.images?.S || []),
  ...(variation.images?.A || []),
];

const referenceKeyElement = (item: mixed): string | null => {
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  return null;
};

export const variationReferenceKeys = (
  paths: $ReadOnlyArray<string>,
  variation: Variation,
): $ReadOnlyArray<string> =>
  pipe(
    map((path) => {
      if (variation) {
        const fetched = get(path.split('.').slice(1).join('.'))(variation);
        return (Array.isArray(fetched) ? fetched : [fetched])
          .map(referenceKeyElement)
          .filter(Boolean);
      }
      return [];
    }),
    flatten,
  )(paths);

export const productReferenceKeys = (
  paths: $ReadOnlyArray<string>,
  product: Product,
  variation?: Variation,
): $ReadOnlyArray<string> =>
  pipe(
    map((path) => {
      switch (path.split('.')[0]) {
        case '$product': {
          if (product) {
            const fetched = get(path.split('.').slice(1).join('.'))(product);
            return (Array.isArray(fetched) ? fetched : [fetched])
              .map(referenceKeyElement)
              .filter(Boolean);
          }
          return [];
        }
        case '$variation': {
          return variation ? variationReferenceKeys([path], variation) : [];
        }
        default:
          return [];
      }
    }),
    flatten,
  )(paths);

export const stockItemCompoundKeyFromParts = ({
  stockItemKey,
  catalogKey,
}: {
  +stockItemKey: string,
  +catalogKey: string,
}): string => `catalog:${catalogKey}:stockItem:${stockItemKey}`;

/**
 * A string that can be used to uniquely identify a StockItem.
 */
export const stockItemCompoundKey = (
  productOrCatalogKey: Product | string,
  { key }: StockItem | StockItemComponent,
): string =>
  `catalog:${
    typeof productOrCatalogKey === 'string'
      ? productOrCatalogKey
      : productOrCatalogKey.catalog_key
  }:stockItem:${key}`;

const maybeCatalogTypeFromContext = (
  catalogAtsModeContext: $ReadOnlyArray<AbstractCatalog>,
  catalogKey: string,
  shipmentsByAvailableStock: boolean,
): null | ResolvedCatalogAtsType => {
  const catalog = catalogAtsModeContext.find(({ key }) => key === catalogKey);
  invariant(catalog, `Catalog must exist for key ${catalogKey}`);
  // resolve this
  return catalogAtsType(shipmentsByAvailableStock)(catalog);
};

/**
 * The `AllocationStrategy` for the given `Variation` and `catalogAtsMode`.
 *
 * This is generally intended to be used with the `useCatalogAtsMode` hook,
 * which handles resolving a catalog ATS mode.
 */
export const allocationStrategy =
  (
    catalogAtsMode: ResolvedCatalogAtsType | null,
  ): ((stockShipment: ItemShipmentStockRecord) => AllocationStrategy) =>
  (stockShipment: ItemShipmentStockRecord): AllocationStrategy => {
    if (catalogAtsMode) {
      switch (catalogAtsMode) {
        case 'blocking':
          return { type: 'blocking' };
        case 'nonblocking':
          return { type: 'unlimited' };
        case 'backorder':
          return { type: 'backorder' };
        case 'unrestricted':
          return { type: 'unrestricted' };
        case 'shipmentsByAvailableStock':
          return { type: 'shipmentsByAvailableStock' };
        default:
          // eslint-disable-next-line babel/no-unused-expressions
          (catalogAtsMode: empty);
          throw new Error(`Unrecognized catalog ATS mode: '${catalogAtsMode}'`);
      }
    }

    const atsCode = stockShipment.atsType;

    if (!atsCode) {
      throw new Error('No discernible ats mode');
    }

    if (atsCode === 'B') return { type: 'blocking' };
    if (atsCode === 'N') return { type: 'unlimited' };
    if (atsCode === 'L') {
      return {
        type: 'dateWindow',
        rules: [
          stockShipment.atsDate
            ? {
                type: 'date',
                date: toDate(stockShipment.atsDate),
              }
            : null,
          typeof stockShipment.atsDays === 'number'
            ? {
                type: 'daysInFuture',
                daysInFuture: stockShipment.atsDays,
              }
            : null,
        ].filter(Boolean),
      };
    }

    throw new Error(`No discernable ATS mode for code '${atsCode.toString()}'`);
  };

/**
 * The `AllocationStrategy` for the given `Variation` and `catalogAtsMode`.
 *
 * This is generally intended to be used with the `useCatalogAtsMode` hook,
 * which handles resolving a catalog ATS mode.
 */

type ReleaseDateFromStockRecord = (
  catalogs: $ReadOnlyArray<AbstractCatalog>,
) => (stockShipment: ItemShipmentStockRecord) => Date | null;

export const releaseDateFromStockRecord: ReleaseDateFromStockRecord =
  (catalogs) => (stockShipment) => {
    const catalog = catalogs.find((c) => c?.key === stockShipment.catalogKey);
    return (
      stockShipment.releaseDate ||
      (catalog && defaultProductReleaseDate(catalog)) ||
      null
    );
  };

export const allocationStrategyGivenUserCatalogs =
  (
    userCatalogs: $ReadOnlyArray<AbstractCatalog> | null,
    shipmentsByAvailableStock: boolean,
  ): ((stockShipment: ItemShipmentStockRecord) => AllocationStrategy) =>
  (stockShipment: ItemShipmentStockRecord): AllocationStrategy => {
    const catalogAtsMode = Array.isArray(userCatalogs)
      ? maybeCatalogTypeFromContext(
          userCatalogs,
          stockShipment.catalogKey,
          shipmentsByAvailableStock,
        )
      : null;

    return allocationStrategy(catalogAtsMode)(stockShipment);
  };

export const csvHeaders = (translate: TranslateType): CsvRow => [
  translate('style_name'),
  translate('style_number'),
  translate('color_name'),
  translate('color_code'),
  translate('size'),
  translate('alt_size'),
  translate('upc'),
  translate('sku'),
  translate('wholesale_price'),
  translate('retail_price'),
  translate('tags'),
];

export const featureCsvHeaders = (translate: TranslateType): CsvRow => [
  translate('style_name'),
  translate('style_number'),
  translate('color_name'),
  translate('color_code'),
  translate('feature_name'),
  translate('feature_value'),
];

export const stockItemTripleAsCsv =
  (
    highlightSpecs: $ReadOnlyArray<HighlightSpec>,
    wholesalePriceGroup: string,
    retailPriceGroup: string,
    tagColumns?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  ): (([Product, Variation, StockItem]) => CsvRow) =>
  ([product, variation, stockItem]: [Product, Variation, StockItem]): CsvRow =>
    [
      product.name,
      product.number,
      variation.name,
      variation.code,
      stockItem.name,
      stockItem.alt_name || '',
      stockItem.upc,
      stockItem.sku,
      stockItem.prices[wholesalePriceGroup],
      stockItem.prices[retailPriceGroup],
      (highlightsForVariationPair(highlightSpecs)([product, variation]) || [])
        .map((h) => h.name)
        .join(', '),
      // tags
      ...(tagColumns && tagColumns.length > 0
        ? tagColumns.map((tag: $ReadOnlyArray<string>): string =>
            get(tag)(pairTagTree([product, variation]))
              ? get(tag)(pairTagTree([product, variation])).join(', ')
              : '',
          )
        : []),
    ];

export const variationAsCsv =
  (
    highlightSpecs: $ReadOnlyArray<HighlightSpec>,
    wholesalePriceGroup: string,
    retailPriceGroup: string,
    tagColumns?: $ReadOnlyArray<$ReadOnlyArray<string>>,
  ): ((VariationPair) => $ReadOnlyArray<CsvRow>) =>
  ([product, variation]: VariationPair): $ReadOnlyArray<CsvRow> =>
    ((variation.stock_items: $ReadOnlyArray<StockItem>).map(
      (si): [Product, Variation, StockItem] => [product, variation, si],
    ): $ReadOnlyArray<[Product, Variation, StockItem]>).map(
      stockItemTripleAsCsv(
        highlightSpecs,
        wholesalePriceGroup,
        retailPriceGroup,
        tagColumns,
      ),
    );

export const asCsv =
  (
    highlightSpecs: $ReadOnlyArray<HighlightSpec>,
    wholesalePriceGroup: string,
    retailPriceGroup: string,
  ): ((product: Product) => $ReadOnlyArray<CsvRow>) =>
  (product: Product): $ReadOnlyArray<CsvRow> =>
    variations(product)
      .map((v) => [product, v])
      .flatMap(
        variationAsCsv(highlightSpecs, wholesalePriceGroup, retailPriceGroup),
      );

export const variationAsFeatureCsv = ([
  product,
  variation,
]: VariationPair): $ReadOnlyArray<CsvRow> =>
  flattenedFeatureListWithParent(pairFeatures([product, variation])).map(
    ([parent, feature]) => [
      product.name,
      product.number,
      variation.name,
      variation.code,
      parent || '',
      feature,
    ],
  );

export const asFeatureCsv = (product: Product): $ReadOnlyArray<CsvRow> =>
  variations(product)
    .map((v) => [product, v])
    .flatMap(variationAsFeatureCsv);

/**
 * The list of StockItemComponents associated with a StockItem.
 */
export const stockItemComponents = (
  stockItem: StockItem,
): $ReadOnlyArray<StockItemComponent> => stockItem.components || [];

/**
 * Predicate: Does the StockItem represent a bundle?
 */
export const stockItemIsBundle = (stockItem: StockItem): boolean =>
  stockItemComponents(stockItem).length > 0;

/**
 * Predicate: Do some of the StockItems in this Variation represent bundles?
 */
export const variationHasBundles = (variation: Variation): boolean =>
  variationStockItems(variation).some(stockItemIsBundle);

/**
 * Predicate: Do all of the StockItems in this Variation represent bundles?
 */
export const variationIsBundle = (variation: Variation): boolean =>
  variationStockItems(variation).every(stockItemIsBundle);

/**
 * Predicate: Do some of the StockItems in this Product represent bundles?
 */
export const hasBundles = (product: Product): boolean =>
  variations(product).some(variationHasBundles);

/**
 * Predicate: Do all of the StockItems in this Product represent bundles?
 */
export const isBundle = (product: Product): boolean =>
  variations(product).every(variationIsBundle);

/**
 * Predicate: Are allocations for this StockItem allocated from the stock
 * of the underlying StockItemComponents, vs from the stock of the bundle
 * itself?
 */
export const isComponentsInventoryMode = (stockItem: StockItem): boolean =>
  stockItemIsBundle(stockItem) && stockItem.inventory_mode === 'components';

/**
 * The number units of the given StockItemComponent that are allocated for
 * each unit allocated of the parent bundle StockItem.
 */
export const unitsPerBundle = (component: StockItemComponent): number =>
  Math.max(component.sold_in_multiples_of || 1, 1);

/**
 * The total number of units allocated per StockItem, accounting for
 * StockItemComponents.
 */
export const stockItemUnits = (stockItem: StockItem): number =>
  stockItemIsBundle(stockItem)
    ? sum(stockItemComponents(stockItem).map(unitsPerBundle))
    : 1;

/**
 * The total number of units allocated per Variation, accounting for
 * StockItemComponents.
 *
 * This is always 1 unless the variation's StockItems all represent
 * bundles with _the same_ number of units.
 */
export const variationUnits = (variation: Variation): number => {
  const unitsPerStockItem = uniq(
    variationStockItems(variation).map(stockItemUnits),
  );

  return unitsPerStockItem.length === 1 ? unitsPerStockItem[0] : 1;
};

/**
 * The total number of units allocated per Product, accounting for
 * StockItemComponents.
 *
 * This is always 1 unless the product's StockItems all represent
 * bundles with _the same_ number of units.
 */
export const productUnits = (product: Product): number => {
  const unitsPerStockItem = uniq(
    variations(product).flatMap((variation) =>
      variationStockItems(variation).map(stockItemUnits),
    ),
  );

  return unitsPerStockItem.length === 1 ? unitsPerStockItem[0] : 1;
};

/**
 * The subset of this Variation's StockItems that represent bundles.
 */
export const variationBundles = (
  variation: Variation,
): $ReadOnlyArray<StockItem> =>
  variationStockItems(variation).filter(stockItemIsBundle);

/**
 * The subset of this Product's StockItems that represent bundles.
 */
export const bundles = (product: Product): $ReadOnlyArray<StockItem> =>
  variations(product).flatMap(variationBundles);

/**
 * The date upon which this Variation is intended to be released to
 * the general public.
 *
 * This is generally the date that the product is actually expected
 * to show up on store shelves. That means that it's also the
 * earliest date that a retailer can schedule to receive this item.
 */
export const variationReleaseDate =
  (catalog: AbstractCatalog): ((variation: Variation) => Date | null) =>
  (variation: Variation): Date | null =>
    latest(
      compact([
        variation.available_on ? toDate(variation.available_on) : null,
        defaultProductReleaseDate(catalog),
      ]),
    );

/**
 * Predicate: Will this Variation have been released by the given date?
 */
export const variationIsReleasedAtDate =
  (
    catalog: AbstractCatalog,
  ): ((date: Date) => (variation: Variation) => boolean) =>
  (date: Date) =>
  (variation: Variation): boolean => {
    const releaseDate = variationReleaseDate(catalog)(variation);
    if (!releaseDate) return true;
    return isAfterOrSame(date, releaseDate);
  };

/**
 * A table for looking up products by the stock item keys they contain.
 *
 * The table actually includes entries for both sku and upc.
 *
 * This doesn't de-dup or anything.
 */
export const productsByStockItemKey = (
  products: $ReadOnlyArray<Product>,
): { +[stockItemKey: string]: $ReadOnlyArray<Product> } => {
  // I'm implementing this using mutation because this function is specifically
  // expected to have large inputs. This is basically just a hash table I'm
  // implementing for optimization, so I want it to be fast.
  const object = { ...null };

  products.forEach((product) => {
    const stockItemKeys = product.variations
      .flatMap((variation) => variation.stock_items)
      .flatMap((si) => uniq([si.key, si.upc, si.sku]));

    stockItemKeys.forEach((key: any) => {
      const current = object[key];
      if (current) {
        // eslint-disable-next-line fp/no-mutating-methods
        current.push(product);
      } else {
        // eslint-disable-next-line fp/no-mutation
        object[key] = [product];
      }
    });
  });

  return object;
};

export const stockItemMatchesUpcOrSku =
  (upcOrSku: string): ((stockItem: StockItem) => boolean) =>
  (stockItem: StockItem) =>
    stockItem.key === upcOrSku ||
    stockItem.upc === upcOrSku ||
    stockItem.sku === upcOrSku;

export const variationPairCompoundKey = ([
  product,
  variation,
]: VariationPair): string =>
  `catalog_key:${product.catalog_key}:product_region:${
    product.region ?? 'null'
  }:product_number:${product.number}:variation_code:${variation.code}`;

export const INDICES: $ReadOnlyArray<Index> = [
  { type: 'Field', property: ['productName'] },
  { type: 'Field', property: ['productNumber'] },
  { type: 'Field', property: ['variationName'] },
  { type: 'Field', property: ['variationCode'] },
];

export const variationPairAsSearchable = ([
  product,
  variation,
]: VariationPair): SearchableVariationPair => ({
  id: variationPairCompoundKey([product, variation]),
  productName: product.name,
  productNumber: product.number,
  variationCode: variation.code,
  variationName: variation.name,
});

/**
 * The position of the Product within the Catalog for sorting.
 */
export const catalogPositionComparator = (product: Product): number =>
  product.position;

/**
 * The name of the Product for sorting.
 */
export const nameComparator = (product: Product): string => product.name;

/**
 * The number of the Product for sorting.
 */
export const numberComparator = (product: Product): string => product.number;

/**
 * The price of the Product for sorting.
 */
export const priceComparator =
  (wholesalePriceGroup: string): ((Product) => number) =>
  (product) =>
    product.price_range[0][wholesalePriceGroup];

/**
 * The position of the VariationPair within the Catalog for sorting.
 *
 * Sorts by variation position within product position.
 */
export const pairCatalogPositionComparator = ([
  product,
  variation,
]: VariationPair): number =>
  catalogPositionComparator(product) + variation.position / 1_000_000;

/**
 * The name of the VariationPair for sorting.
 *
 * Sorts by variation name within product name.
 */
export const pairNameComparator = ([
  product,
  variation,
]: VariationPair): string => `${nameComparator(product)}:::${variation.name}`;

/**
 * The number of the VariationPair for sorting.
 *
 * Sorts by variation position within product number.
 */
export const pairNumberComparator = ([
  product,
  variation,
]: VariationPair): string =>
  `${numberComparator(product)}:::${String(variation.position).padStart(
    20,
    '0',
  )}`;

/**
 * The price of the VariationPair for sorting.
 */
export const pairPriceComparator =
  (wholesalePriceGroup: string): ((VariationPair) => string) =>
  ([product, variation]) =>
    `${String(product.price_range[0][wholesalePriceGroup]).padStart(
      20,
      '0',
    )}:::;${String(variation.position).padStart(20, '0')}`;

/**
 * A comparator for sorting Products.
 */
export const comparator =
  (wholesalePriceGroup: string): ((SortOrder) => Comparator) =>
  // eslint isn't aware that this check is exhaustive
  // eslint-disable-next-line consistent-return
  (order: SortOrder) => {
    // eslint isn't aware that this check is exhaustive
    // eslint-disable-next-line default-case
    switch (order) {
      case SortOrder.Catalog:
        return catalogPositionComparator;
      case SortOrder.Name:
        return nameComparator;
      case SortOrder.ProductNumber:
        return numberComparator;
      case SortOrder.Price:
        return priceComparator(wholesalePriceGroup);
    }
  };

/**
 * A comparator for sorting VariationPairs.
 */
export const pairComparator =
  (wholesalePriceGroup: string): ((SortOrder) => VariationPairComparator) =>
  // eslint isn't aware that this check is exhaustive
  // eslint-disable-next-line consistent-return
  (order: SortOrder) => {
    // eslint isn't aware that this check is exhaustive
    // eslint-disable-next-line default-case
    switch (order) {
      case SortOrder.Catalog:
        return pairCatalogPositionComparator;
      case SortOrder.Name:
        return pairNameComparator;
      case SortOrder.ProductNumber:
        return pairNumberComparator;
      case SortOrder.Price:
        return pairPriceComparator(wholesalePriceGroup);
    }
  };

export const sorter: (
  wholesalePriceGroup: string,
) => (SortOrder) => ('asc' | 'desc') => (ROA<Product>) => ROA<Product> =
  (wholesalePriceGroup) => (order) => (direction) => (list) =>
    orderBy([comparator(wholesalePriceGroup)(order)])(direction)(list);

export const pairSorter: (
  wholesalePriceGroup: string,
) => (SortOrder) => (
  'asc' | 'desc',
) => (ROA<VariationPair>) => ROA<VariationPair> =
  (wholesalePriceGroup) => (order) => (direction) => (list) =>
    orderBy([pairComparator(wholesalePriceGroup)(order)])(direction)(list);
